import { useEffect, useMemo, useState } from 'react';
import { specialLabelsFor } from './specialDates';
import { API_BASE, BOOKINGS_PUBLISHED_CSV_URL } from './config';
import { csvToObjects } from './csv';
import { useTheme, tokens } from './theme';

// Liberal truthiness check for yes-like values
function isYes(v) {
  if (v === undefined || v === null) return false;
  const s = String(v).trim().toLowerCase();
  if (s === '') return false;
  return !['no', 'false', '0', 'n'].includes(s);
}

function displayNameFromRow(row) {
  return (
    row.name ||
    row.Name ||
    row.memberName ||
    row.MemberName ||
    row['Member Name'] ||
    row.member_number ||
    row.memberNumber ||
    'Unknown'
  );
}

function fmtDate(ymd) {
  try {
    const [y, m, d] = ymd.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return ymd;
  }
}

export default function BookingSummary() {
  const [theme] = useTheme();
  const t = tokens(theme);

  const [bookings, setBookings] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    // Prefer the published-sheet CSV (no API quota, CDN-cached, ~5 min stale).
    // Fall back to SheetDB if no CSV URL is configured.
    const useCsv = Boolean(BOOKINGS_PUBLISHED_CSV_URL);

    const loadData = useCsv
      ? fetch(BOOKINGS_PUBLISHED_CSV_URL).then((res) => {
          if (!res.ok) throw new Error('CSV fetch failed: ' + res.status);
          return res.text().then((text) => csvToObjects(text));
        })
      : fetch(API_BASE).then((res) => {
          if (!res.ok) throw new Error('Network response was not ok');
          return res.json();
        });

    loadData
      .then((data) => {
        if (cancelled) return;
        const upcoming = {};
        const today = new Date().toISOString().split('T')[0];

        data.forEach((row) => {
          const date = row.date || row.Date;
          if (!date) return;
          if (date < today) return;
          const memberNumber = row.memberNumber || row['Member Number'] || '';
          if (typeof memberNumber === 'string' && memberNumber.startsWith('CD-')) return;

          const name = displayNameFromRow(row);
          if (!upcoming[date]) {
            upcoming[date] = { breakfast: [], lunch: [], dinner: [] };
          }
          if (isYes(row.breakfast || row.Breakfast)) upcoming[date].breakfast.push(name);
          if (isYes(row.lunch || row.Lunch)) upcoming[date].lunch.push(name);
          if (isYes(row.dinner || row.Dinner)) upcoming[date].dinner.push(name);
        });

        setBookings(upcoming);
        setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || String(e));
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const sortedDates = useMemo(() => {
    const keys = Object.keys(bookings);
    return keys.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  }, [bookings]);

  const baseStyle = { fontFamily: 'Arial', fontSize: 14, color: t.text };

  if (loading) {
    return <div style={baseStyle}>Loading…</div>;
  }
  if (error) {
    return <div style={{ ...baseStyle, color: t.danger }}>Error: {error}</div>;
  }
  if (sortedDates.length === 0) {
    return <div style={baseStyle}>No upcoming bookings.</div>;
  }

  const th = { borderBottom: `1px solid ${t.borderStrong}`, textAlign: 'left', paddingBottom: 6, color: t.textStrong };
  const td = { borderBottom: `1px solid ${t.border}`, padding: 4 };

  return (
    <div style={baseStyle}>
      <h2 style={{ margin: '6px 0 10px', color: t.textStrong }}>Booking Summary</h2>

      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          marginTop: 10,
          fontFamily: 'Arial',
          fontSize: 14,
        }}
      >
        <thead>
          <tr>
            <th style={th}>Date</th>
            <th style={th}>Breakfast</th>
            <th style={th}>Lunch</th>
            <th style={th}>Dinner</th>
          </tr>
        </thead>
        <tbody>
          {sortedDates.map((date) => {
            const spec = specialLabelsFor(date);
            const bNames = bookings[date].breakfast;
            const lNames = bookings[date].lunch;
            const dNames = bookings[date].dinner;

            const cell = (meal, names) => {
              const labelText = spec.label[meal];
              const isEnabled = spec.enabled[meal];
              if (!isEnabled || !labelText) {
                return <span style={{ opacity: 0.5 }}>—</span>;
              }
              return (
                <span>
                  <span style={{ fontWeight: 600 }}>{labelText}:</span>{' '}
                  {names && names.length ? Array.from(new Set(names)).join(', ') : '—'}
                </span>
              );
            };

            return (
              <tr key={date}>
                <td style={td}>{fmtDate(date)}</td>
                <td style={td}>{cell('breakfast', bNames)}</td>
                <td style={td}>{cell('lunch', lNames)}</td>
                <td style={td}>{cell('dinner', dNames)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
